import { observer } from 'mobx-react-lite'
import { useEffect, useState, useCallback } from 'react'
import { useHistory, useParams, Link, Prompt } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Group, Box } from '@mantine/core'
import { FiUpload } from 'react-icons/fi'
import Breadcrumb from '../../app/common/Breadcrumb'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { AppButton, AppCard, AppInput, AppSwitch, AppText, AppTextarea, ProfileUserAvatar } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { toast } from 'react-toastify'
import PhotoUploadCropModal from './PhotoUploadCropModal'

const validationSchema = Yup.object({
  displayName: Yup.string().required('Display name is required').max(200, 'Max 200 characters'),
  bio: Yup.string().max(500, 'Max 500 characters'),
  coverImageUrl: Yup.string()
    .max(2000, 'Max 2000 characters')
    .test('url', 'Must be a valid URL', (value) => !value || value.trim() === '' || /^https?:\/\/.+/i.test(value.trim()))
})

export default observer(function ProfileEditForm () {
  const history = useHistory()
  const { username } = useParams<{ username: string }>()
  const { profileStore, userStore } = useStore()
  const { profile, loadProfile, updateProfile, uploadPhoto, loadingProfile, uploading } = profileStore
  const [avatarFile, setAvatarFile] = useState<File | null>(null)

  useEffect(() => {
    if (username) loadProfile(username)
  }, [username, loadProfile])

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) setAvatarFile(accepted[0])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg,image/jpg,image/png,image/webp,image/gif',
    maxFiles: 1,
    multiple: false,
    maxSize: 5 * 1024 * 1024,
    onDropRejected: (fileRejections) => {
      const err = fileRejections[0]?.errors[0]
      if (err?.code === 'file-too-large') toast.error('Image must be under 5MB')
      else toast.error(err?.message || 'Invalid image')
    }
  })

  const handleAvatarCrop = async (blob: Blob) => {
    await uploadPhoto(blob, true)
    setAvatarFile(null)
  }

  if (!userStore.isLoggedIn) {
    history.push('/login')
    return null
  }

  if (loadingProfile || !profile) {
    return <LoadingComponent content="Loading profile..." />
  }

  if (profile.username !== username) {
    return <LoadingComponent content="Loading profile..." />
  }

  if (!profileStore.canEdit) {
    toast.error('You cannot edit this profile')
    history.push(`/profiles/${username}`)
    return null
  }

  const breadcrumbItems = [
    { label: 'Profile', href: `/profiles/${username}` },
    { label: 'Edit' }
  ]

  return (
    <Box sx={{ maxWidth: 720, margin: '0 auto', padding: 'var(--gs-xl) var(--gs-md)' }}>
      <Breadcrumb items={breadcrumbItems} />
      <Group position="apart" mb="lg">
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22 }}>Edit profile</AppText>
        <AppButton component={Link} to={`/profiles/${username}`} variant="subtle">
          Cancel
        </AppButton>
      </Group>

      <AppCard p="lg" radius="md" withBorder>
        <Box mb="xl">
          <AppText size="sm" weight={500} mb="xs">
            Profile photo
          </AppText>
          <Group spacing="md">
            <ProfileUserAvatar
              variant="header"
              size={100}
              imageUrl={profile.image}
              displayName={profile.displayName}
              username={profile.username}
              tier={profile.ammoTier}
            />
            <Box
              {...getRootProps()}
              sx={{
                padding: 12,
                border: '2px dashed var(--gs-border-bright)',
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'rgba(232,93,4,0.08)' : undefined,
                transition: 'background-color 0.15s ease'
              }}
            >
              <input {...getInputProps()} />
              <Group spacing="xs">
                <FiUpload size={20} />
                <AppText size="sm" color="dimmed">
                  Drop image or click to change
                </AppText>
              </Group>
            </Box>
          </Group>
        </Box>

        <Formik
          validationSchema={validationSchema}
          enableReinitialize
          initialValues={{
            displayName: profile.displayName,
            bio: profile.bio ?? '',
            coverImageUrl: profile.coverImageUrl ?? '',
            isVaultPublic: profile.isVaultPublic !== false,
            isWishlistPublic: profile.isWishlistPublic !== false
          }}
          onSubmit={async (values, { resetForm }) => {
            await updateProfile(
              values.displayName,
              values.bio || undefined,
              values.coverImageUrl?.trim() || null,
              values.isVaultPublic,
              values.isWishlistPublic
            )
            toast.success('Profile updated')
            resetForm({ values })
            history.push(`/profiles/${username}`)
          }}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty, values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
            <>
              <Prompt when={dirty} message="You have unsaved changes. Leave anyway?" />
              <Form onSubmit={handleSubmit}>
              <AppInput
                label="Display name"
                name="displayName"
                value={values.displayName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.displayName && errors.displayName}
                placeholder="Your display name"
                required
                mb="md"
              />
              <AppTextarea
                label="Bio"
                name="bio"
                value={values.bio}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.bio && errors.bio}
                placeholder="Tell us about yourself (optional)"
                minRows={4}
                mb="md"
              />
              <Box mb="md">
                <AppSwitch
                  label="Public vault"
                  description="Allow others to see your Gun Vault from your profile."
                  checked={values.isVaultPublic}
                  onChange={(e) => setFieldValue('isVaultPublic', e.currentTarget.checked)}
                />
              </Box>
              <Box mb="md">
                <AppSwitch
                  label="Public wishlist"
                  description="When off, your wishlist is only visible to you. Individual items can still be hidden below."
                  checked={values.isWishlistPublic}
                  onChange={(e) => setFieldValue('isWishlistPublic', e.currentTarget.checked)}
                />
              </Box>
              <AppInput
                label="Cover image URL"
                name="coverImageUrl"
                value={values.coverImageUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.coverImageUrl && errors.coverImageUrl}
                placeholder="https://…"
                description="Optional. Paste an image URL to use as your profile cover. Leave blank to keep current or clear."
                mb="lg"
              />
              <AppButton type="submit" disabled={isSubmitting || !dirty || !isValid} loading={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save changes'}
              </AppButton>
            </Form>
            </>
          )}
        </Formik>
      </AppCard>

      <PhotoUploadCropModal
        file={avatarFile}
        open={!!avatarFile}
        onClose={() => setAvatarFile(null)}
        onCrop={handleAvatarCrop}
        loading={uploading}
      />
    </Box>
  )
})
